import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, of, catchError } from 'rxjs';
import { User, CreatorExample } from '../models/user.model';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private apiUrl = `${environment.apiUrl}/users`;
  private creatorApiUrl = `${environment.apiUrl}/creator`;
  private http = inject(HttpClient);

  // Cache the user data
  private currentUser: User | null = null;

  /**
   * Upload W-9 PDF
   */
  uploadW9(file: File) {
    const fd = new FormData();
    // Append with explicit filename to preserve extension for server-side validation
    fd.append('w9', file, file.name);
    return this.http.post<{ status: string; has_w9: boolean; message?: string }>(`${this.apiUrl}/w9`, fd);
  }

  /**
   * Upload up to 5 creator example videos (mp4, mov, webm) to Laravel (no Box)
   */
  uploadCreatorExamples(files: File[]): Observable<CreatorExample[]> {
    const fd = new FormData();
    const allowed = ['video/mp4', 'video/quicktime', 'video/webm'];
    const maxSize = 250 * 1024 * 1024; // 250MB

    const toSend = files.slice(0, 5).filter(f => {
      const okType = allowed.includes(f.type) || /\.(mp4|mov|webm)$/i.test(f.name);
      const okSize = f.size <= maxSize;
      return okType && okSize;
    });

    toSend.forEach(f => fd.append('files[]', f, f.name));
    return this.http.post<CreatorExample[]>(`${this.creatorApiUrl}/examples`, fd);
  }

  /**
   * List current creator's uploaded examples
   */
  getCreatorExamples(): Observable<CreatorExample[]> {
    return this.http.get<CreatorExample[]>(`${this.creatorApiUrl}/examples`);
  }

  /**
   * Delete a specific creator example by id
   */
  deleteCreatorExample(id: number): Observable<void> {
    return this.http.delete<void>(`${this.creatorApiUrl}/examples/${id}`);
  }

  /**
   * Get the current user's profile
   */
  getCurrentUser(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/profile`).pipe(
      map(user => {
        const processedUser = this.processProfileImage(user);
        this.currentUser = processedUser;
        return processedUser;
      }),
      catchError(error => {
        console.error('[UserService] Error fetching user profile:', error);
        return of(null as unknown as User);
      })
    );
  }

  /**
   * Update the current user's profile
   */
  updateProfile(userData: Partial<User>): Observable<User> {
    // Make a copy of the user data to avoid modifying the original
    const userToSend = { ...userData };

    // If the profile_image is a data URL, extract the base64 part
    if (userToSend.profile_image && userToSend.profile_image.startsWith('data:')) {
      const parts = userToSend.profile_image.split(',');
      if (parts.length === 2) {
        userToSend.profile_image = parts[1];
      }
    }

    return this.http.put<User>(`${this.apiUrl}/profile`, userToSend).pipe(
      map(user => {
        const processedUser = this.processProfileImage(user);
        this.currentUser = processedUser;
        return processedUser;
      }),
      catchError(error => {
        console.error('Error updating user profile:', error);
        return of(null as unknown as User);
      })
    );
  }

  /**
   * Complete the creator profile and set is_complete flag to true
   */
  completeCreatorProfile(userData: Partial<User>): Observable<User> {
    // Make a copy of the user data to avoid modifying the original
    const userToSend = { ...userData };

    // If the profile_image is a data URL, extract the base64 part
    if (userToSend.profile_image && userToSend.profile_image.startsWith('data:')) {
      const parts = userToSend.profile_image.split(',');
      if (parts.length === 2) {
        userToSend.profile_image = parts[1];
      }
    }

    return this.http.post<User>(`${this.apiUrl}/complete-profile`, userToSend).pipe(
      map(user => {
        const processedUser = this.processProfileImage(user);
        this.currentUser = processedUser;
        return processedUser;
      }),
      catchError(error => {
        console.error('Error completing creator profile:', error);
        return of(null as unknown as User);
      })
    );
  }

  /**
   * Check if the current user is a content creator with an incomplete profile
   */
  isCreatorWithIncompleteProfile(): Observable<boolean> {
    // If we already have the user data, use it
    if (this.currentUser) {
      const isCreatorWithIncompleteProfile = !!this.currentUser.roles &&
        this.currentUser.roles.includes('creator') &&
        this.currentUser.profile_complete === false;

      return of(isCreatorWithIncompleteProfile);
    }

    // Otherwise, fetch the user data
    return this.getCurrentUser().pipe(
      map(user => {
        if (!user) {
          return false;
        }

        const isCreatorWithIncompleteProfile = !!user.roles &&
               user.roles.includes('creator') &&
               user.profile_complete === false;

        return isCreatorWithIncompleteProfile;
      })
    );
  }

  /**
   * Process profile image URLs to ensure they are properly formatted
   */
  private processProfileImage(user: User): User {
    if (user.profile_image) {
      // If it's already a data URL or a full URL, return as is
      if (user.profile_image.startsWith('data:') ||
          user.profile_image.startsWith('http://') ||
          user.profile_image.startsWith('https://')) {
        return user;
      }

      // If it's a relative path, prepend the API base URL
      if (user.profile_image.startsWith('/')) {
        const apiBaseUrl = environment.apiUrl.split('/api')[0]; // Get base URL without /api
        user.profile_image = apiBaseUrl + user.profile_image;
      } else {
        // If it's just a filename or path without leading slash, assume it's in storage
        const apiBaseUrl = environment.apiUrl.split('/api')[0]; // Get base URL without /api
        user.profile_image = `${apiBaseUrl}/storage/${user.profile_image}`;
      }
    }
    return user;
  }
}
